import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type System, type PickerSuggestionsResponse } from '@bliks-io/contracts';
import { SystemCard } from '@bliks-io/ui';
import { EntityPickerBucketsList, type BucketSection } from '../shared/EntityPickerBucketsList';
import { buildBucketSections } from '../shared/buildBucketSections';

export interface SystemPickerBucketsProps {
  systems: System[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  mode: 'single' | 'multi';
  suggestions: PickerSuggestionsResponse['data'] | undefined;
  recentlyUsedLimit?: number;
  /**
   * Synthetic `System` rows for pending suggestions. The hook builds them via
   * `buildPendingSystemItems`, so each carries a `__pending__:`-prefixed `_id`.
   * Surfaced as a dedicated bucket section so they read as peers of real
   * systems but stay visually grouped.
   */
  pendingItems?: System[];
}

export function SystemPickerBuckets({
  systems,
  selectedIds,
  onToggle,
  mode,
  suggestions,
  recentlyUsedLimit = 5,
  pendingItems
}: SystemPickerBucketsProps) {
  const { t } = useTranslation();
  const byId = useMemo(() => new Map(systems.map((s) => [s._id, s] as const)), [systems]);

  const sections = useMemo<BucketSection<System>[]>(() => {
    const built = buildBucketSections<System>({
      byId,
      suggestions,
      t,
      recentlyUsedLimit,
      config: {
        byRoleTitle: (roleName) =>
          roleName
            ? `${t('entityPicker.system.buckets.byRole')} · ${roleName}`
            : t('entityPicker.system.buckets.byRole'),
        byRoleSubtitle: (peerCount) =>
          t('entityPicker.system.buckets.byRolePeerCount', {
            count: peerCount
          }),
        byDepartmentTitle: (departmentName) =>
          departmentName
            ? `${t('entityPicker.system.buckets.byDepartment')} · ${departmentName}`
            : t('entityPicker.system.buckets.byDepartment'),
        byDepartmentSubtitle: (peerCount) =>
          t('entityPicker.system.buckets.byDepartmentPeerCount', {
            count: peerCount
          }),
        byProcessTitle: (processName) =>
          processName
            ? `${t('entityPicker.system.buckets.byProcess')} · ${processName}`
            : t('entityPicker.system.buckets.byProcess'),
        byProcessSubtitle: (sampleSize) =>
          t('entityPicker.system.buckets.byProcessSampleSize', {
            count: sampleSize
          })
      }
    });
    // Pending suggestions sit at the top: they're the rows the user is most
    // likely to revisit immediately after creating one. Hidden when empty so
    // the section doesn't leave a stranded header.
    if (pendingItems && pendingItems.length > 0) {
      built.unshift({
        key: 'pendingSuggestions',
        title: t('entityPicker.common.buckets.pendingSuggestions'),
        items: pendingItems
      });
    }
    return built;
  }, [byId, suggestions, t, recentlyUsedLimit, pendingItems]);

  return (
    <EntityPickerBucketsList<System>
      sections={sections}
      selectedIds={selectedIds}
      onToggle={onToggle}
      mode={mode}
      renderItem={(system) => (
        <SystemCard system={system} variant="with-category" itemVariant="default" size="xs" />
      )}
    />
  );
}
